import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, CalendarIcon, RefreshCw } from "lucide-react";
import { format, isPast } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface VoucherRecord {
  id: string;
  code: string;
  discount_type: "fixed" | "percent" | "free";
  // Phase 4 split (Sprint D PR-AA): percent type's value lives in discount_percent,
  // fixed type's value (in öre) in discount_value_minor. Legacy discount_value is
  // nullable post-PR-AA migration and read-only fallback.
  discount_value: number | null;
  discount_percent: number | null;
  discount_value_minor: number | null;
  currency: string;
  max_uses: number | null;
  max_per_user: number | null;
  uses_count: number | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
  [key: string]: unknown;
}

/**
 * Phase 4-aware read of a voucher's display value.
 * Returns the canonical kr/percent number based on type, preferring Phase 4
 * cols and falling back to legacy discount_value for old rows.
 */
function readVoucherValue(v: Pick<VoucherRecord, "discount_type" | "discount_value" | "discount_percent" | "discount_value_minor">): number {
  if (v.discount_type === "percent") {
    return v.discount_percent ?? v.discount_value ?? 0;
  }
  if (v.discount_type === "fixed") {
    return v.discount_value_minor != null
      ? v.discount_value_minor / 100
      : (v.discount_value ?? 0);
  }
  // free: no numeric value
  return 0;
}

function generateCode(len = 8): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

export default function AdminVouchers() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const qc = useQueryClient();

  const { data: vouchers = [], isLoading } = useQuery({
    queryKey: ["admin-vouchers", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vouchers" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as VoucherRecord[] | null) ?? [];
    },
    enabled: !!organization?.id,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editVoucher, setEditVoucher] = useState<VoucherRecord | null>(null);
  const [form, setForm] = useState({
    code: "",
    discount_type: "fixed" as "fixed" | "percent" | "free",
    discount_value: 0,
    max_uses: null as number | null,
    max_per_user: 1,
    valid_from: null as Date | null,
    valid_until: null as Date | null,
    is_active: true,
  });

  const openNew = () => {
    setEditVoucher(null);
    setForm({
      code: generateCode(),
      discount_type: "fixed",
      discount_value: 100,
      max_uses: null,
      max_per_user: 1,
      valid_from: null,
      valid_until: null,
      is_active: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (v: VoucherRecord) => {
    setEditVoucher(v);
    // Phase 4-aware read: load form value from the matching Phase 4 col,
    // falling back to legacy for old rows that pre-date the split.
    setForm({
      code: v.code,
      discount_type: v.discount_type,
      discount_value: readVoucherValue(v),
      max_uses: v.max_uses,
      max_per_user: v.max_per_user ?? 1,
      valid_from: v.valid_from ? new Date(v.valid_from) : null,
      valid_until: v.valid_until ? new Date(v.valid_until) : null,
      is_active: v.is_active,
    });
    setDialogOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Sprint D Phase 4: write ONLY the matching Phase 4 col + currency,
      // omitting legacy discount_value entirely (made nullable in
      // migration 20260425210309). Defensive trigger from PR #20 backfills
      // _minor from legacy IF caller provides legacy — we don't, so trigger
      // is a no-op for new writes from this UI. The matching Phase 4 col is
      // always set for percent/fixed types; free type has both NULL.
      const isPercent = form.discount_type === "percent";
      const isFixed = form.discount_type === "fixed";
      const payload: Record<string, unknown> = {
        organization_id: organization!.id,
        code: form.code.toUpperCase().trim(),
        discount_type: form.discount_type,
        // Phase 4 cols (the source of truth post-PR-AA):
        discount_percent: isPercent ? Math.round(form.discount_value) : null,
        discount_value_minor: isFixed ? Math.round(form.discount_value * 100) : null,
        currency: "SEK",
        max_uses: form.max_uses,
        max_per_user: form.max_per_user,
        valid_from: form.valid_from?.toISOString() ?? null,
        valid_until: form.valid_until?.toISOString() ?? null,
        is_active: form.is_active,
      };
      // Note: NOT writing legacy discount_value. New rows have discount_value=NULL
      // (column was made nullable in PR-AA). Existing rows keep their legacy values.
      if (editVoucher?.id) {
        // On UPDATE, also clear legacy so the row converges to Phase 4 source-of-truth.
        payload.discount_value = null;
        const { error } = await supabase.from("vouchers" as any).update(payload).eq("id", editVoucher.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("vouchers" as any).insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-vouchers"] });
      setDialogOpen(false);
      toast.success(editVoucher ? "Kampanjkod uppdaterad" : "Kampanjkod skapad");
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte spara kampanjkod"); },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vouchers" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-vouchers"] });
      setDialogOpen(false);
      toast.success(t("admin:toast.voucherDeleted"));
    },
  });

  const getStatus = (v: VoucherRecord): { label: string; variant: "default" | "secondary" | "destructive" } => {
    if (!v.is_active) return { label: "Inaktiv", variant: "secondary" };
    if (v.valid_until && isPast(new Date(v.valid_until))) return { label: "Utgången", variant: "destructive" };
    if (v.max_uses && (v.uses_count ?? 0) >= v.max_uses) return { label: "Förbrukad", variant: "secondary" };
    return { label: "Aktiv", variant: "default" };
  };

  const formatDiscount = (v: VoucherRecord) => {
    if (v.discount_type === "free") return "Gratis";
    // Phase 4-aware: read from matching Phase 4 col, fallback to legacy for old rows.
    const value = readVoucherValue(v);
    if (v.discount_type === "percent") return `${value}%`;
    return `${value} kr`;
  };

  if (isLoading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Kampanjkoder</h3>
        <Button size="sm" onClick={openNew}>
          <Plus className="h-4 w-4 mr-1" /> Ny kampanjkod
        </Button>
      </div>

      {vouchers.length === 0 ? (
        <p className="text-sm text-muted-foreground">Inga kampanjkoder skapade ännu.</p>
      ) : (
        <div className="space-y-2">
          {vouchers.map((v) => {
            const status = getStatus(v);
            return (
              <Card key={v.id} className="cursor-pointer hover:bg-accent/30 transition-colors" onClick={() => openEdit(v)}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <span className="font-mono font-bold text-sm tracking-wide">{v.code}</span>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>Rabatt: {formatDiscount(v)}</span>
                      <span>·</span>
                      <span>Använt: {v.uses_count ?? 0}{v.max_uses ? ` / ${v.max_uses}` : ""}</span>
                      {v.valid_from || v.valid_until ? (
                        <>
                          <span>·</span>
                          <span>
                            {v.valid_from ? format(new Date(v.valid_from), "d MMM", { locale: sv }) : "—"}
                            {" – "}
                            {v.valid_until ? format(new Date(v.valid_until), "d MMM", { locale: sv }) : "—"}
                          </span>
                        </>
                      ) : (
                        <>
                          <span>·</span>
                          <span>Alltid</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge variant={status.variant}>{status.label}</Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editVoucher ? "Redigera kampanjkod" : "Ny kampanjkod"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Code */}
            <div className="space-y-1">
              <Label>Kod</Label>
              <div className="flex gap-2">
                <Input
                  value={form.code}
                  onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="BLACKFRIDAY"
                  className="font-mono uppercase"
                />
                <Button type="button" variant="outline" size="icon" onClick={() => setForm(f => ({ ...f, code: generateCode() }))}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Discount type + value */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Rabattyp</Label>
                <div className="flex rounded-md border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, discount_type: "fixed", discount_value: 100 }))}
                    className={cn("flex-1 px-3 py-1.5 text-sm font-medium transition-colors",
                      form.discount_type === "fixed" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"
                    )}
                  >
                    kr
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, discount_type: "percent", discount_value: 20 }))}
                    className={cn("flex-1 px-3 py-1.5 text-sm font-medium transition-colors border-l",
                      form.discount_type === "percent" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"
                    )}
                  >
                    %
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, discount_type: "free", discount_value: 0 }))}
                    className={cn("flex-1 px-3 py-1.5 text-sm font-medium transition-colors border-l",
                      form.discount_type === "free" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"
                    )}
                  >
                    Gratis
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {/* Phase 4 split: type-aware label + clamping. Same input field, but
                    saveMutation routes the value to discount_percent (percent) or
                    discount_value_minor (fixed). Switching type resets to a sane
                    default (20% / 100 kr / 0) so values never leak across types. */}
                <Label>
                  {form.discount_type === "percent" ? "Procent (0–100)" :
                   form.discount_type === "fixed" ? "Belopp (kr)" :
                   "Värde"}
                </Label>
                <NumberInput
                  value={form.discount_value}
                  onValueChange={(raw) => {
                    // Clamp percent to 0-100; allow any non-negative for fixed.
                    const clamped = form.discount_type === "percent"
                      ? Math.min(Math.max(raw, 0), 100)
                      : Math.max(raw, 0);
                    setForm(f => ({ ...f, discount_value: clamped }));
                  }}
                  disabled={form.discount_type === "free"}
                  placeholder={form.discount_type === "percent" ? "20" : "100"}
                  min={0}
                  max={form.discount_type === "percent" ? 100 : undefined}
                />
              </div>
            </div>

            {/* Max uses */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Max användningar totalt</Label>
                {/* Pre-existing bug fix: NumberInput uses onValueChange, not onChange.
                    Original onChange was being silently dropped (Omit'd from props).
                    emptyValue=0 sentinel treated as "unlimited" (null in DB). */}
                <NumberInput
                  value={form.max_uses ?? 0}
                  onValueChange={(v) => setForm(f => ({ ...f, max_uses: v > 0 ? v : null }))}
                  placeholder="Obegränsat"
                />
              </div>
              <div className="space-y-1">
                <Label>Max per kund</Label>
                <NumberInput
                  value={form.max_per_user}
                  onValueChange={(v) => setForm(f => ({ ...f, max_per_user: v > 0 ? v : 1 }))}
                />
              </div>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Giltig från</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !form.valid_from && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.valid_from ? format(form.valid_from, "d MMM yyyy", { locale: sv }) : "Valfritt"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.valid_from ?? undefined}
                      onSelect={(d) => setForm(f => ({ ...f, valid_from: d ?? null }))}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <Label>Giltig till</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !form.valid_until && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.valid_until ? format(form.valid_until, "d MMM yyyy", { locale: sv }) : "Valfritt"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.valid_until ?? undefined}
                      onSelect={(d) => setForm(f => ({ ...f, valid_until: d ?? null }))}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Active */}
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))} />
              <Label>Aktiv</Label>
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <div>
              {editVoucher && (
                <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate(editVoucher.id)} disabled={deleteMutation.isPending}>
                  <Trash2 className="h-4 w-4 mr-1" /> Ta bort
                </Button>
              )}
            </div>
            <Button onClick={() => saveMutation.mutate()} disabled={!form.code.trim() || saveMutation.isPending}>
              {saveMutation.isPending ? <Loader2 className="animate-spin h-4 w-4 mr-1" /> : null}
              Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
